// src/composer/hooks/useAnimationFrame.ts
import { useEffect, useRef } from 'react';
import type { LifecycleAdapter } from '../../adapters/types';

export function useAnimationFrame(
  onTick: (t: number) => void,
  lifecycle: LifecycleAdapter,
) {
  const rafRef = useRef<number>(undefined);
  const startRef = useRef<number>(undefined);
  const tPauseRef = useRef(0);
  const pausedRef = useRef(false);
  const onTickRef = useRef(onTick);
  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  useEffect(() => {
    if (lifecycle.prefersReducedMotion()) {
      onTickRef.current(0);
      return;
    }

    const tick = (now: number) => {
      if (!startRef.current) startRef.current = now;
      if (!pausedRef.current) {
        const t = (now - startRef.current) / 1000;
        tPauseRef.current = t;
        onTickRef.current(t);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const unsubVis = lifecycle.onVisibilityChange(visible => {
      pausedRef.current = !visible;
      if (visible && startRef.current !== undefined) {
        // Re-base start so t continues from where it was when paused
        startRef.current = performance.now() - tPauseRef.current * 1000;
      }
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      unsubVis();
    };
  }, [lifecycle]);
}
